/**
 * BreadcrumbContext — lets a page override the LAST breadcrumb label.
 *
 * Use case: `/projects/{id}` should render the actual project name in
 * the breadcrumb instead of the generic word "Project". The
 * `ProjectDetail` page calls `useSetBreadcrumb(project.name)` on mount;
 * the `PageBreadcrumb` reads `lastCrumbOverride` and uses it for the
 * trailing (non-link) crumb.
 *
 * Cleared automatically on unmount so the next page doesn't inherit a
 * stale override.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

type Ctx = {
  lastCrumbOverride: string | null;
  setLastCrumbOverride: (label: string | null) => void;
};

const BreadcrumbCtx = createContext<Ctx>({
  lastCrumbOverride: null,
  setLastCrumbOverride: () => {},
});

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [lastCrumbOverride, setLastCrumbOverride] = useState<string | null>(null);
  const value = useMemo(
    () => ({ lastCrumbOverride, setLastCrumbOverride }),
    [lastCrumbOverride],
  );
  return <BreadcrumbCtx.Provider value={value}>{children}</BreadcrumbCtx.Provider>;
}

export function useBreadcrumbOverride() {
  return useContext(BreadcrumbCtx).lastCrumbOverride;
}

/** Page-level helper: set the override on mount, clear on unmount. */
export function useSetBreadcrumb(label: string | null | undefined) {
  const { setLastCrumbOverride } = useContext(BreadcrumbCtx);
  const stable = useCallback(setLastCrumbOverride, [setLastCrumbOverride]);
  useEffect(() => {
    if (!label) return;
    stable(label);
    return () => stable(null);
  }, [label, stable]);
}
